import { t, onLangChange } from "./i18n.js";
import { getDiffMinimap, onDiffMinimapChange } from "./diffMinimap.js";
import { buildDiffView, buildContentView, attachMinimaps, buildChangeNav, MM_W } from "./diffView.js";
import { attachDiffFind, resetDiffFind } from "./diffFind.js";
import { getContentSearch, setContentSearch } from "./contentSearchSetting.js";
import type { CommitChangeFile, DiffFileStatus, FileDiff } from "@rev-graph/protocol";

/**
 * The TortoiseSVN-style "Show changes" dialog: the left pane lists the files a
 * commit touched with two tabs:
 *  - "Changed": only the files this commit modified (grouped by status)
 *  - "All Files": every file in the commit's tree, with changed ones still marked
 * The right pane shows the selected file's diff (for changed files) or its raw
 * content (for unchanged files).
 *
 * The dialog is data-driven by host messages handled in main.ts:
 *  - `commitChanges`   → setChangesFiles() fills the changed-files list,
 *  - `commitTree`      → setCommitTree() fills the all-files list,
 *  - `fileDiff`        → setFileDiff() fills the right pane for a changed file,
 *  - `fileContent`     → setFileContent() fills the right pane for an unchanged file,
 *  - `treeContentSearchResults` → setTreeContentSearchResults() adds the files whose
 *    content contains the search term (content search, toggleable).
 * Selecting a changed file calls back into main.ts (onRequestFile).
 * Selecting an unchanged file calls back (onRequestFileContent).
 */
export interface ChangesDialogContext {
  sha: string;
  onRequestFile: (file: CommitChangeFile) => void;
  onRequestFileContent: (path: string) => void;
  /** Ask the host which files in the commit's tree contain `query` (git grep). */
  onSearchTreeContent: (query: string) => void;
}

/** Shortest query that triggers a host-side content search (1 char would match nearly everything). */
const MIN_CONTENT_QUERY = 2;

/** Status priority used to pick the auto-selected first file. */
const STATUS_ORDER: DiffFileStatus[] = ["added", "modified", "renamed", "deleted"];
/** Single-letter badge per status. */
const STATUS_MARK: Record<DiffFileStatus, string> = {
  added: "A",
  modified: "M",
  deleted: "D",
  renamed: "R",
};

/** A node in the file tree: either a folder (with children) or a file leaf. */
interface FileTreeNode {
  name: string;
  path: string;
  isFolder: boolean;
  /** The change entry, on changed file leaves only. */
  file?: CommitChangeFile;
  children: FileTreeNode[];
}

/**
 * Build a folder tree from a flat list of file paths. Changed files carry their
 * CommitChangeFile; unchanged files have no `file` property.
 */
function buildFileTree(
  paths: string[],
  changedByPath: Map<string, CommitChangeFile>,
): FileTreeNode[] {
  const root: FileTreeNode = { name: "", path: "", isFolder: true, children: [] };
  for (const filePath of paths) {
    const segments = filePath.split("/").filter(Boolean);
    if (segments.length === 0) continue;
    let parent = root;
    for (let i = 0; i < segments.length - 1; i++) {
      const seg = segments[i]!;
      const path = parent.path ? `${parent.path}/${seg}` : seg;
      let folder = parent.children.find((c) => c.isFolder && c.name === seg);
      if (!folder) {
        folder = { name: seg, path, isFolder: true, children: [] };
        parent.children.push(folder);
      }
      parent = folder;
    }
    const leaf = segments[segments.length - 1]!;
    parent.children.push({
      name: leaf,
      path: filePath,
      isFolder: false,
      file: changedByPath.get(filePath),
      children: [],
    });
  }
  sortTree(root);
  return root.children;
}

/** Sort folders before files, each alphabetically (case-insensitive), in place. */
function sortTree(node: FileTreeNode): void {
  node.children.sort((a, b) => {
    if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
  for (const child of node.children) sortTree(child);
}

/** Recursively collect every folder path in a tree into `into`. */
function collectFolderPaths(nodes: FileTreeNode[], into: Set<string>): void {
  for (const node of nodes) {
    if (!node.isFolder) continue;
    into.add(node.path);
    collectFolderPaths(node.children, into);
  }
}

function normalizeQuery(query: string): string {
  return query.trim().toLowerCase();
}

/** True when a file's path contains the (already-lowercased) query. */
function pathMatchesQuery(path: string, query: string): boolean {
  return path.toLowerCase().includes(query);
}

/** True when a changed file's cached diff text contains the query — lets a
 * search for a method/identifier surface the file that touched it, once its
 * diff has been fetched (see fetchMissingDiffs). */
function diffMatchesQuery(path: string, query: string, cache: Map<string, FileDiff>): boolean {
  const cached = cache.get(path);
  if (!cached || cached.binary || cached.tooLarge) return false;
  return cached.oldText.toLowerCase().includes(query) || cached.newText.toLowerCase().includes(query);
}

/**
 * Wrap every occurrence of `query` inside the rendered diff/content code with a
 * <mark class="changes-search-hit"> so the term the user searched for is visible
 * where it actually appears. Walks text nodes only (leaving highlight.js's own
 * spans intact) and works on the already-rendered DOM. Returns the first mark so
 * the caller can scroll it into view; null when nothing matched.
 */
function highlightSearchHits(root: HTMLElement, query: string): HTMLElement | null {
  const q = query.toLowerCase();
  if (!q) return null;
  let first: HTMLElement | null = null;
  const cells = root.querySelectorAll<HTMLElement>(".diff-code");
  cells.forEach((cell) => {
    // Collect text nodes up front — we mutate the tree as we go.
    const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) textNodes.push(node as Text);
    for (const textNode of textNodes) {
      const text = textNode.nodeValue ?? "";
      const lower = text.toLowerCase();
      if (!lower.includes(q)) continue;
      const frag = document.createDocumentFragment();
      let last = 0;
      let idx = lower.indexOf(q);
      while (idx >= 0) {
        if (idx > last) frag.appendChild(document.createTextNode(text.slice(last, idx)));
        const mark = document.createElement("mark");
        mark.className = "changes-search-hit";
        mark.textContent = text.slice(idx, idx + q.length);
        if (!first) first = mark;
        frag.appendChild(mark);
        last = idx + q.length;
        idx = lower.indexOf(q, last);
      }
      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
      textNode.parentNode?.replaceChild(frag, textNode);
    }
  });
  return first;
}

const FOLDER_SVG =
  '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
  '<path fill="currentColor" d="M1.5 3h4l1.2 1.6h7.8a.5.5 0 0 1 .5.5v8.4a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5V3.5A.5.5 0 0 1 1.5 3z"/></svg>';
const FILE_SVG =
  '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
  '<path fill="currentColor" d="M9.5 1H3.5a.5.5 0 0 0-.5.5v13a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5V5L9.5 1z"/>' +
  '<path fill="rgba(0,0,0,0.35)" d="M9.5 1L13 5H10a.5.5 0 0 1-.5-.5V1z"/></svg>';
/** "Collapse all folders" toolbar icon — a double chevron pointing up (fold shut). */
const COLLAPSE_ALL_SVG =
  '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
  '<path fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" d="M4 7.5 8 4l4 3.5M4 12l4-3.5 4 3.5"/></svg>';
/** "Expand all folders" toolbar icon — a double chevron pointing down (fold open). */
/** "Search in file contents" toggle icon — curly braces (code). */
const CONTENT_SEARCH_SVG =
  '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
  '<path fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" ' +
  'd="M5.5 2.5c-1.5 0-2 .6-2 2v1.6c0 .9-.5 1.4-1.5 1.9 1 .5 1.5 1 1.5 1.9v1.6c0 1.4.5 2 2 2M10.5 2.5c1.5 0 2 .6 2 2v1.6c0 .9.5 1.4 1.5 1.9-1 .5-1.5 1-1.5 1.9v1.6c0 1.4-.5 2-2 2"/></svg>';
const EXPAND_ALL_SVG =
  '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
  '<path fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" d="M4 4.5 8 8l4-3.5M4 9l4 3.5 4-3.5"/></svg>';
/** Maximize icon — a single outlined square. */
const MAXIMIZE_SVG =
  '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
  '<rect x="2.5" y="2.5" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';
/** Restore icon — two overlapping squares. */
const RESTORE_SVG =
  '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
  '<rect x="2.5" y="4.5" width="9" height="9" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
  '<path fill="none" stroke="currentColor" stroke-width="1.4" d="M5.5 4.5V2.5h8v8h-2"/></svg>';

function fileCategory(name: string): string {
  const ext = name.slice(name.lastIndexOf(".") + 1).toLowerCase();
  if (["js", "mjs", "cjs", "jsx"].includes(ext)) return "js";
  if (["ts", "tsx"].includes(ext)) return "ts";
  if (ext === "json") return "json";
  if (["md", "markdown"].includes(ext)) return "md";
  if (["css", "scss", "less"].includes(ext)) return "css";
  if (["html", "htm"].includes(ext)) return "html";
  if (ext === "cs") return "cs";
  if (["png", "jpg", "jpeg", "gif", "svg", "ico", "webp"].includes(ext)) return "img";
  return "default";
}

let openOverlay: HTMLElement | null = null;
let langUnsub: (() => void) | null = null;
let minimapUnsub: (() => void) | null = null;
let minimapCleanup: (() => void) | null = null;
/** Tears down the Ctrl/Cmd+F find box of the current diff; null when none. */
let findCleanup: (() => void) | null = null;

// Dialog state, module-scoped so host-message handlers can update it.
let ctx: ChangesDialogContext | null = null;
/** Changed files (null = still loading). */
let files: CommitChangeFile[] | null = null;
/** All paths in the commit's tree (null = not yet requested/received). */
let allPaths: string[] | null = null;
/** Active tab. */
let activeTab: "changed" | "all" = "changed";
/** Selected changed file (drives the diff pane). */
let selected: CommitChangeFile | null = null;
/** Selected unchanged file path (drives the content pane). */
let selectedPath: string | null = null;
/** Diff for the selected changed file. */
let diff: FileDiff | null = null;
/** Content for the selected unchanged file. */
interface FileContentState {
  text: string;
  binary?: boolean;
  tooLarge?: boolean;
}
let fileContent: FileContentState | null = null;
let listEl: HTMLElement | null = null;
let diffPaneEl: HTMLElement | null = null;
let collapsed = new Set<string>();
let listWidth: number | null = null;
/** Whether the modal currently fills the viewport. */
let maximized = false;
/** Current file-search query (filters both tabs; also drives content search on "Changed"). */
let searchQuery = "";
/** Diffs fetched so far, keyed by path — populated by setFileDiff, used for content search. */
const diffCache = new Map<string, FileDiff>();
/** Paths we've already asked the host for a diff of, to avoid duplicate requests while searching. */
const requestedDiffPaths = new Set<string>();
let searchDebounce: ReturnType<typeof setTimeout> | undefined;
/** Host `git grep` hits for the current query: files in the commit's tree whose content matches. */
let treeContentMatches: { query: string; paths: Set<string> } | null = null;

export function closeChangesDialog(): void {
  if (openOverlay) {
    openOverlay.remove();
    openOverlay = null;
  }
  if (langUnsub) { langUnsub(); langUnsub = null; }
  if (minimapUnsub) { minimapUnsub(); minimapUnsub = null; }
  minimapCleanup?.();
  minimapCleanup = null;
  findCleanup?.();
  findCleanup = null;
  resetDiffFind();
  clearTimeout(searchDebounce);
  ctx = null;
  files = null;
  allPaths = null;
  activeTab = "changed";
  selected = null;
  selectedPath = null;
  diff = null;
  fileContent = null;
  listEl = null;
  diffPaneEl = null;
  collapsed = new Set();
  listWidth = null;
  maximized = false;
  searchQuery = "";
  treeContentMatches = null;
  diffCache.clear();
  requestedDiffPaths.clear();
}

export function openChangesDialog(context: ChangesDialogContext): void {
  closeChangesDialog();
  ctx = context;

  const overlay = document.createElement("div");
  overlay.className = "settings-overlay";
  const modal = document.createElement("div");
  modal.className = "settings-modal changes-modal";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  overlay.appendChild(modal);

  function toggleMaximize(): void {
    maximized = !maximized;
    modal.classList.toggle("maximized", maximized);
    render();
  }

  function render(): void {
    if (!ctx) return;
    modal.innerHTML = "";
    modal.classList.toggle("maximized", maximized);

    // ---- Header ----
    const header = el("div", "settings-modal-header");
    header.appendChild(el("span", "settings-modal-title", t("changes.title", { sha: ctx.sha.slice(0, 7) })));
    // Window controls, grouped together on the right (Windows-style): maximize
    // sits directly next to the close button rather than floating in the middle.
    const controls = el("div", "settings-modal-controls");
    const maxBtn = button("settings-close-x changes-max-btn", "", toggleMaximize);
    maxBtn.innerHTML = maximized ? RESTORE_SVG : MAXIMIZE_SVG;
    const maxLabel = maximized ? t("changes.restore") : t("changes.maximize");
    maxBtn.title = maxLabel;
    maxBtn.setAttribute("aria-label", maxLabel);
    controls.appendChild(maxBtn);
    const closeBtn = button("settings-close-x", "×", closeChangesDialog);
    closeBtn.setAttribute("aria-label", t("changes.close"));
    controls.appendChild(closeBtn);
    header.appendChild(controls);
    modal.appendChild(header);

    // ---- Body: file list (left) + diff (right) ----
    const body = el("div", "changes-body");

    const list = el("div", "changes-list");
    if (listWidth != null) list.style.flexBasis = `${listWidth}px`;

    // Tabs row
    const tabs = el("div", "changes-tabs");
    const changedCount = files !== null ? files.length : null;
    const changedLabel =
      changedCount !== null
        ? `${t("changes.tabChanged")} (${changedCount})`
        : t("changes.tabChanged");
    const tabChanged = button(
      "changes-tab" + (activeTab === "changed" ? " changes-tab-active" : ""),
      changedLabel,
      () => {
        if (activeTab === "changed") return;
        activeTab = "changed";
        selectedPath = null;
        fileContent = null;
        renderList();
        renderDiff();
        renderTabs();
        runContentSearch(false);
      },
    );
    const tabAll = button(
      "changes-tab" + (activeTab === "all" ? " changes-tab-active" : ""),
      t("changes.tabAll"),
      () => {
        if (activeTab === "all") return;
        activeTab = "all";
        selected = null;
        diff = null;
        renderList();
        renderDiff();
        renderTabs();
        runContentSearch(false);
      },
    );
    tabs.append(tabChanged, tabAll);
    list.appendChild(tabs);

    // Search + collapse-all toolbar row.
    const toolbar = el("div", "changes-list-toolbar");
    const searchInput = document.createElement("input");
    searchInput.type = "text";
    searchInput.className = "settings-input changes-search-input";
    searchInput.placeholder = t("changes.searchPlaceholder");
    searchInput.value = searchQuery;
    searchInput.addEventListener("input", (e) => {
      const value = (e.target as HTMLInputElement).value;
      clearTimeout(searchDebounce);
      searchDebounce = setTimeout(() => {
        searchQuery = value;
        renderList();
        runContentSearch(true);
      }, 180);
    });
    searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        // Clear the search box without closing the whole dialog.
        e.stopPropagation();
        if (searchInput.value || searchQuery) {
          searchInput.value = "";
          clearTimeout(searchDebounce);
          searchQuery = "";
          treeContentMatches = null;
          renderList();
        } else {
          searchInput.blur();
        }
      }
    });
    toolbar.appendChild(searchInput);
    // Toggle: also match file contents (methods, identifiers), not just names.
    const contentOn = getContentSearch();
    const contentBtn = button(
      "changes-toolbar-btn changes-content-toggle" + (contentOn ? " active" : ""),
      "",
      () => {
        setContentSearch(!getContentSearch());
        render();
        runContentSearch(true);
      },
    );
    contentBtn.innerHTML = CONTENT_SEARCH_SVG;
    const contentLabel = contentOn ? t("changes.contentSearchOn") : t("changes.contentSearchOff");
    contentBtn.title = contentLabel;
    contentBtn.setAttribute("aria-label", contentLabel);
    contentBtn.setAttribute("aria-pressed", String(contentOn));
    toolbar.appendChild(contentBtn);
    // Dedicated folder controls — always present, regardless of whether the tree
    // has any folders (they simply no-op on a flat file list).
    const collapseBtn = button("changes-toolbar-btn", "", collapseAllFolders);
    collapseBtn.innerHTML = COLLAPSE_ALL_SVG;
    collapseBtn.title = t("changes.collapseAll");
    collapseBtn.setAttribute("aria-label", t("changes.collapseAll"));
    toolbar.appendChild(collapseBtn);
    const expandBtn = button("changes-toolbar-btn", "", expandAllFolders);
    expandBtn.innerHTML = EXPAND_ALL_SVG;
    expandBtn.title = t("changes.expandAll");
    expandBtn.setAttribute("aria-label", t("changes.expandAll"));
    toolbar.appendChild(expandBtn);
    list.appendChild(toolbar);

    listEl = el("div", "changes-list-scroll");
    list.appendChild(listEl);
    renderList();

    const resizer = el("div", "changes-resizer");
    attachResizer(resizer, list);

    const diffPane = el("div", "changes-diff");
    diffPaneEl = diffPane;
    renderDiff();

    body.append(list, resizer, diffPane);
    modal.appendChild(body);

    // Keep a reference to the tabs container so renderTabs() can update it.
    currentTabsEl = tabs;
    currentTabChangedBtn = tabChanged;
    currentTabAllBtn = tabAll;
  }

  function attachResizer(handle: HTMLElement, list: HTMLElement): void {
    handle.addEventListener("mousedown", (down) => {
      down.preventDefault();
      const startX = down.clientX;
      const startW = list.getBoundingClientRect().width;
      const onMove = (move: MouseEvent): void => {
        const next = Math.max(140, Math.min(640, startW + (move.clientX - startX)));
        listWidth = next;
        list.style.flexBasis = `${next}px`;
      };
      const onUp = (): void => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        document.body.style.cursor = "";
      };
      document.body.style.cursor = "col-resize";
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    });
  }

  function renderList(): void {
    if (!listEl) return;
    listEl.innerHTML = "";

    if (activeTab === "changed") {
      renderChangedTab();
    } else {
      renderAllTab();
    }
  }

  /**
   * True when `path` matches the current (normalized) query: by filename always;
   * with content search on, also when its cached diff text (old or new side — so
   * removed code is found too) or the host's `git grep` over the commit's tree hits.
   */
  function pathMatches(path: string, query: string): boolean {
    if (pathMatchesQuery(path, query)) return true;
    if (!getContentSearch()) return false;
    if (diffMatchesQuery(path, query, diffCache)) return true;
    return treeContentMatches?.query === query && treeContentMatches.paths.has(path);
  }

  /** Changed files matching the current search — by filename, or (content search) file content. */
  function visibleChangedFiles(): CommitChangeFile[] {
    if (!files) return [];
    const query = normalizeQuery(searchQuery);
    if (!query) return files;
    return files.filter((f) => pathMatches(f.path, query));
  }

  /**
   * All-tree paths matching the current search. Every file matches by filename;
   * with content search on, any file whose content contains the query matches too
   * (the host greps the whole commit tree, so a method search reveals which file
   * defines it), plus changed files whose cached diff contains it.
   */
  function visibleAllPaths(): string[] {
    if (!allPaths) return [];
    const query = normalizeQuery(searchQuery);
    if (!query) return allPaths;
    return allPaths.filter((p) => pathMatches(p, query));
  }

  /**
   * Kick off the content part of the search (when enabled): pull any not-yet-fetched
   * diffs of changed files, and — when `grep` is set — ask the host to grep the
   * commit's tree for the query. Tab switches pass `grep=false`: the grep result
   * is tab-independent and already cached for the current query.
   */
  function runContentSearch(grep: boolean): void {
    const query = normalizeQuery(searchQuery);
    if (!query || !getContentSearch()) return;
    fetchMissingDiffs();
    if (grep && query.length >= MIN_CONTENT_QUERY && treeContentMatches?.query !== query) {
      ctx?.onSearchTreeContent(searchQuery.trim());
    }
  }

  function renderChangedTab(): void {
    if (!listEl) return;
    if (files === null) {
      listEl.appendChild(el("div", "changes-empty", t("changes.loading")));
      return;
    }
    if (files.length === 0) {
      listEl.appendChild(el("div", "changes-empty", t("changes.noChanges")));
      return;
    }
    const visible = visibleChangedFiles();
    if (visible.length === 0) {
      listEl.appendChild(el("div", "changes-empty", t("changes.noSearchResults")));
      return;
    }
    // Build tree from the (search-filtered) changed files only.
    const changedByPath = new Map<string, CommitChangeFile>(visible.map((f) => [f.path, f]));
    const tree = el("div", "changes-tree");
    for (const node of buildFileTree(visible.map((f) => f.path), changedByPath))
      renderNode(node, tree, 0, true);
    listEl.appendChild(tree);
  }

  function renderAllTab(): void {
    if (!listEl) return;
    if (allPaths === null) {
      listEl.appendChild(el("div", "changes-empty", t("changes.loading")));
      return;
    }
    const visible = visibleAllPaths();
    if (visible.length === 0 && normalizeQuery(searchQuery)) {
      listEl.appendChild(el("div", "changes-empty", t("changes.noSearchResults")));
      return;
    }
    const changedByPath = new Map<string, CommitChangeFile>(
      (files ?? []).map((f) => [f.path, f]),
    );
    const tree = el("div", "changes-tree");
    for (const node of buildFileTree(visible, changedByPath))
      renderNode(node, tree, 0, false);
    listEl.appendChild(tree);
  }

  /** Fetch diffs for every changed file not yet cached, so content search can match them. */
  function fetchMissingDiffs(): void {
    if (!ctx || !files) return;
    for (const f of files) {
      if (diffCache.has(f.path) || requestedDiffPaths.has(f.path)) continue;
      requestedDiffPaths.add(f.path);
      ctx.onRequestFile(f);
    }
  }

  /** Collapse every folder currently visible in the active tab's (search-filtered) tree. */
  function collapseAllFolders(): void {
    const changedByPath = new Map<string, CommitChangeFile>(
      (files ?? []).map((f) => [f.path, f]),
    );
    const paths = activeTab === "changed" ? visibleChangedFiles().map((f) => f.path) : visibleAllPaths();
    collectFolderPaths(buildFileTree(paths, changedByPath), collapsed);
    renderList();
  }

  /** Expand every folder — simply forget all collapsed paths. */
  function expandAllFolders(): void {
    if (collapsed.size === 0) return;
    collapsed.clear();
    renderList();
  }

  function renderNode(
    node: FileTreeNode,
    container: HTMLElement,
    depth: number,
    changedOnly: boolean,
  ): void {
    if (node.isFolder) {
      container.appendChild(folderRow(node, depth, changedOnly));
      if (!collapsed.has(node.path)) {
        for (const child of node.children)
          renderNode(child, container, depth + 1, changedOnly);
      }
    } else {
      if (node.file) {
        container.appendChild(fileRow(node.file, node.name, depth));
      } else {
        container.appendChild(staticFileRow(node.path, node.name, depth));
      }
    }
  }

  function folderRow(node: FileTreeNode, depth: number, changedOnly: boolean): HTMLElement {
    const row = el("div", "changes-folder");
    row.style.paddingLeft = `${6 + depth * 14}px`;
    const isCollapsed = collapsed.has(node.path);
    row.appendChild(el("span", "changes-chevron", isCollapsed ? "▸" : "▾"));
    const icon = el("span", "changes-icon changes-icon-folder");
    icon.innerHTML = FOLDER_SVG;
    row.appendChild(icon);
    row.appendChild(el("span", "changes-folder-name", node.name));
    row.addEventListener("click", () => {
      if (collapsed.has(node.path)) collapsed.delete(node.path);
      else collapsed.add(node.path);
      renderList();
    });
    return row;
  }

  function fileRow(file: CommitChangeFile, label: string, depth: number): HTMLElement {
    const row = el("div", "changes-file");
    row.dataset.status = file.status;
    row.style.paddingLeft = `${6 + depth * 14}px`;
    const isSelected =
      selectedPath === null &&
      selected &&
      selected.path === file.path &&
      selected.status === file.status;
    if (isSelected) row.classList.add("selected");
    const icon = el("span", `changes-icon changes-icon-${fileCategory(label)}`);
    icon.innerHTML = FILE_SVG;
    const name = el("span", "changes-file-name", label);
    name.title = file.oldPath ? t("changes.renamedFrom", { path: file.oldPath }) : file.path;
    const mark = el("span", `changes-mark changes-mark-${file.status}`, STATUS_MARK[file.status]);
    row.append(icon, name, mark);
    row.addEventListener("click", () => selectChangedFile(file));
    return row;
  }

  /** A row for a file that exists in the tree but was NOT changed by this commit. */
  function staticFileRow(filePath: string, label: string, depth: number): HTMLElement {
    const row = el("div", "changes-file changes-file-static");
    row.style.paddingLeft = `${6 + depth * 14}px`;
    if (selectedPath === filePath) row.classList.add("selected");
    const icon = el("span", `changes-icon changes-icon-${fileCategory(label)}`);
    icon.innerHTML = FILE_SVG;
    const name = el("span", "changes-file-name", label);
    name.title = filePath;
    row.append(icon, name);
    row.addEventListener("click", () => selectUnchangedFile(filePath));
    return row;
  }

  function selectChangedFile(file: CommitChangeFile): void {
    selected = file;
    selectedPath = null;
    fileContent = null;
    diff = null;
    renderList();
    renderDiff();
    ctx?.onRequestFile(file);
  }

  function selectUnchangedFile(path: string): void {
    selectedPath = path;
    selected = null;
    diff = null;
    fileContent = null;
    renderList();
    renderDiff();
    ctx?.onRequestFileContent(path);
  }

  function renderDiff(): void {
    if (!diffPaneEl) return;
    findCleanup?.();
    findCleanup = null;
    diffPaneEl.innerHTML = "";
    const scroll = el("div", "changes-diff-scroll");
    const showEmpty = (key: Parameters<typeof t>[0]): void => {
      scroll.appendChild(el("div", "changes-empty", t(key)));
      diffPaneEl!.appendChild(scroll);
    };

    if (selectedPath !== null) {
      // Viewing an unchanged file — show its raw content with optional minimap.
      if (!fileContent) return showEmpty("changes.loading");
      if (fileContent.binary) return showEmpty("changes.binary");
      if (fileContent.tooLarge) return showEmpty("changes.tooLarge");
      const minimapOn = getDiffMinimap();
      const { view, minimaps } = buildContentView(selectedPath, fileContent.text, minimapOn);
      scroll.appendChild(view);
      diffPaneEl.appendChild(scroll);
      minimapCleanup?.();
      minimapCleanup = minimapOn ? attachMinimaps(diffPaneEl, scroll, minimaps) : null;
      applySearchHighlight(scroll);
      findCleanup = attachDiffFind(diffPaneEl, scroll, minimapOn ? MM_W + 10 : 14);
      return;
    }

    if (!selected) return showEmpty("changes.selectFile");
    if (!diff) return showEmpty("changes.loading");
    if (diff.binary) return showEmpty("changes.binary");
    if (diff.tooLarge) return showEmpty("changes.tooLarge");

    const minimapOn = getDiffMinimap();
    const { view, blocks, minimaps } = buildDiffView(diff, minimapOn);
    scroll.appendChild(view);
    diffPaneEl.appendChild(scroll);
    minimapCleanup?.();
    minimapCleanup = minimapOn ? attachMinimaps(diffPaneEl, scroll, minimaps) : null;
    if (blocks.length > 1) {
      diffPaneEl.appendChild(buildChangeNav(scroll, blocks, minimapOn ? MM_W + 10 : 14));
    }
    applySearchHighlight(scroll);
    findCleanup = attachDiffFind(diffPaneEl, scroll, minimapOn ? MM_W + 10 : 14);
  }

  /** Mark the active search term in the just-rendered diff/content and reveal the first hit. */
  function applySearchHighlight(scroll: HTMLElement): void {
    const q = normalizeQuery(searchQuery);
    if (!q || !getContentSearch()) return;
    const firstHit = highlightSearchHits(scroll, q);
    if (firstHit) firstHit.scrollIntoView({ block: "center", inline: "nearest" });
  }

  render();
  langUnsub = onLangChange(render);
  minimapUnsub = onDiffMinimapChange(() => renderDiff());

  overlay.addEventListener("mousedown", (e) => {
    if (e.target === overlay) closeChangesDialog();
  });
  document.body.appendChild(overlay);
  openOverlay = overlay;

  pendingRenderList = renderList;
  pendingRenderDiff = renderDiff;
  pendingRenderTabs = renderTabs;
}

// References to the tab buttons, updated by render() so renderTabs() can update
// them without re-building the entire dialog.
let currentTabsEl: HTMLElement | null = null;
let currentTabChangedBtn: HTMLButtonElement | null = null;
let currentTabAllBtn: HTMLButtonElement | null = null;

/** Update only the tab button labels/states without re-rendering the whole dialog. */
function renderTabs(): void {
  if (!currentTabChangedBtn || !currentTabAllBtn) return;
  const changedCount = files !== null ? files.length : null;
  const changedLabel =
    changedCount !== null
      ? `${t("changes.tabChanged")} (${changedCount})`
      : t("changes.tabChanged");
  currentTabChangedBtn.textContent = changedLabel;
  currentTabChangedBtn.className =
    "changes-tab" + (activeTab === "changed" ? " changes-tab-active" : "");
  currentTabAllBtn.textContent = t("changes.tabAll");
  currentTabAllBtn.className =
    "changes-tab" + (activeTab === "all" ? " changes-tab-active" : "");
}

let pendingRenderList: (() => void) | null = null;
let pendingRenderDiff: (() => void) | null = null;
let pendingRenderTabs: (() => void) | null = null;

export function setChangesFiles(sha: string, incoming: CommitChangeFile[]): void {
  if (!ctx || ctx.sha !== sha) return;
  files = incoming;
  pendingRenderTabs?.();
  if (activeTab === "changed") pendingRenderList?.();
  // Auto-select the first changed file so the user immediately sees a diff.
  if (incoming.length > 0 && !selected && !selectedPath) {
    const first = orderedFirst(incoming);
    if (first) {
      selected = first;
      diff = null;
      pendingRenderList?.();
      pendingRenderDiff?.();
      ctx.onRequestFile(first);
    }
  }
}

export function setCommitTree(sha: string, paths: string[]): void {
  if (!ctx || ctx.sha !== sha) return;
  allPaths = paths;
  if (activeTab === "all") pendingRenderList?.();
}

export function setTreeContentSearchResults(sha: string, query: string, paths: string[]): void {
  if (!ctx || ctx.sha !== sha) return;
  const q = normalizeQuery(query);
  // Drop stale answers — the user has typed on since this grep was issued.
  if (q !== normalizeQuery(searchQuery)) return;
  treeContentMatches = { query: q, paths: new Set(paths) };
  pendingRenderList?.();
}

export function setFileDiff(incoming: FileDiff): void {
  if (!ctx || ctx.sha !== incoming.sha) return;
  // Cache every diff we see (not just the currently selected one) so content search can
  // match files the user hasn't opened yet — see fetchMissingDiffs.
  diffCache.set(incoming.path, incoming);
  if (selected && selected.path === incoming.path) {
    diff = incoming;
    pendingRenderDiff?.();
  }
  // A newly-arrived diff may make a file match an active content search, on either tab.
  if (normalizeQuery(searchQuery)) pendingRenderList?.();
}

export function setFileContent(
  sha: string,
  path: string,
  text: string,
  binary?: boolean,
  tooLarge?: boolean,
): void {
  if (!ctx || ctx.sha !== sha) return;
  if (selectedPath !== path) return;
  fileContent = { text, binary, tooLarge };
  pendingRenderDiff?.();
}

function orderedFirst(list: CommitChangeFile[]): CommitChangeFile | null {
  for (const status of STATUS_ORDER) {
    const hit = list.find((f) => f.status === status);
    if (hit) return hit;
  }
  return list[0] ?? null;
}

/**
 * A simple single-column file-content viewer using the same diff grid CSS,
 * but with neutral (no red/green) line styling — for unchanged files.
 */

function el(tag: string, className: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}
function button(className: string, text: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = className;
  b.textContent = text;
  b.addEventListener("click", onClick);
  return b;
}

window.addEventListener("keydown", (e) => {
  if (e.key === "Escape") closeChangesDialog();
});
